import React from "react";
import { Box } from "@mui/material";
import { tokens } from "../theme";
import EmptyState from "./EmptyState";

/**
 * A listing whose rows are a hierarchy.
 *
 * Why this is not DataTable. DataTable's row model is flat and deliberately so:
 * one record per row, the same cells in every row, one shape of row throughout.
 * A tree row breaks all three. Its cells depend on what kind of thing the row
 * is — a unit has a headcount, a person has a role and no count of their own,
 * a truncation row has a sentence and nothing else — and its meaning depends on
 * a depth that DataTable has no notion of. Bending DataTable to it would mean
 * adding depth, per-row variants and column spanning to a component whose value
 * is that it has none of those, and every flat listing in the product would
 * carry the weight. So the tree gets its own small component, and DataTable
 * stays the thing it is good at.
 *
 * What it shares with DataTable is what matters: columns are declared as data,
 * it owns all four states, and it draws its own card shell, so a tree listing
 * and a flat one sit on the same ground with the same header band.
 *
 * Layout is a CSS grid rather than a <table>, because the indent is a property
 * of the first cell's content and a table cell that shifts its own contents by
 * depth is exactly the hand-rolled table this codebase keeps deleting.
 *
 *   <TreeTable
 *     columns={[{ key: "name", label: "Department and team" }, …]}
 *     rows={flattenOrgTree(root, { expanded })}
 *     renderCell={(row, col) => …}
 *     rowVariant={(row) => (row.depth === 0 ? "band" : "plain")}
 *   />
 */

const VARIANTS = {
  // A top-level unit. The page blush behind it is what makes the tree's spine
  // visible without drawing a single rule.
  band: { background: tokens.surface, fontWeight: 600 },
  plain: { background: "transparent", fontWeight: 400 },
  // The summary. Heavier, and on the blush, because it is the sum of
  // everything above it rather than another item in the list.
  total: { background: tokens.surface, fontWeight: 700 },
};

const gridSx = (columns) => ({
  display: "grid",
  gridTemplateColumns: columns.map((c) => c.width || "1fr").join(" "),
  gap: "0.875rem",
  alignItems: "center",
});

const TreeTable = ({
  columns = [],
  rows = [],
  getRowId = (row, i) => row?.rowId ?? i,
  renderCell,
  rowVariant = () => "plain",
  indent = 26,
  loading,
  error,
  onRetry,
  errorTitle = "Couldn't load this list",
  empty,
  onRowClick,
  ariaLabel,
}) => {
  const Shell = ({ children }) => (
    <Box
      sx={{
        background: tokens.card,
        border: `1px solid ${tokens.border}`,
        borderRadius: "10px",
        overflow: "hidden",
      }}
      role="table"
      aria-label={ariaLabel}
    >
      {children}
    </Box>
  );

  const head = (
    <Box
      role="row"
      sx={{
        ...gridSx(columns),
        p: "0.6875rem 1.125rem",
        background: tokens.surface,
        borderBottom: `1px solid ${tokens.border}`,
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: tokens.textMuted,
      }}
    >
      {columns.map((c) => (
        <Box key={c.key} role="columnheader" sx={{ textAlign: c.align || "left" }}>
          {c.label}
        </Box>
      ))}
    </Box>
  );

  // The header and the column widths are known before the data is, so they
  // render immediately and nothing shifts when the rows arrive.
  if (loading) {
    return (
      <Shell>
        {head}
        <Box aria-busy="true">
          {Array.from({ length: 5 }, (_, i) => (
            <Box
              key={i}
              sx={{
                ...gridSx(columns),
                p: "0.6875rem 1.125rem",
                borderBottom: i === 4 ? "none" : `1px solid ${tokens.border}`,
              }}
            >
              {columns.map((c, j) => (
                <Box key={c.key} sx={{ height: "1.4rem", display: "flex", alignItems: "center" }}>
                  <Box className="pl-skeleton" sx={{ width: ["86%", "62%", "48%", "74%"][(i + j) % 4] }} />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Shell>
    );
  }

  // Failed and empty replace the list: a header over nothing reads as a list
  // that lost its rows. Failure is never red — a read that did not work is not
  // the reader's mistake.
  if (error) return <EmptyState variant="error" title={errorTitle} onRetry={onRetry} />;
  if (!rows.length) return <EmptyState {...(empty || { title: "Nothing here yet" })} />;

  return (
    <Shell>
      {head}
      {rows.map((row, i) => {
        const variant = VARIANTS[rowVariant(row, i)] || VARIANTS.plain;
        const clickable = Boolean(onRowClick);
        const last = i === rows.length - 1;
        return (
          <Box
            key={getRowId(row, i)}
            role="row"
            aria-level={typeof row.depth === "number" ? row.depth + 1 : undefined}
            aria-expanded={row.expandable ? Boolean(row.expanded) : undefined}
            tabIndex={clickable && row.expandable ? 0 : undefined}
            onClick={clickable ? () => onRowClick(row) : undefined}
            onKeyDown={
              clickable && row.expandable
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }
                : undefined
            }
            sx={{
              ...gridSx(columns),
              p: "0.6875rem 1.125rem",
              fontSize: "0.875rem",
              color: tokens.text,
              background: variant.background,
              fontWeight: variant.fontWeight,
              borderBottom: last ? "none" : `1px solid ${tokens.border}`,
              cursor: clickable && row.expandable ? "pointer" : "default",
              transition: (theme) =>
                theme.transitions.create("background-color", {
                  duration: theme.transitions.duration.shortest,
                }),
              ...(clickable && row.expandable
                ? { "&:hover": { background: tokens.surfaceSubtle } }
                : {}),
            }}
          >
            {columns.map((c, colIndex) => (
              <Box
                key={c.key}
                role="cell"
                sx={{
                  textAlign: c.align || "left",
                  minWidth: 0,
                  // Depth is the first column's business: the indent is what
                  // says where in the hierarchy this row sits.
                  ...(colIndex === 0 && row.depth ? { pl: `${row.depth * indent}px` } : {}),
                  ...c.cellSx,
                }}
              >
                {renderCell ? renderCell(row, c) : row?.[c.key] ?? null}
              </Box>
            ))}
          </Box>
        );
      })}
    </Shell>
  );
};

export default TreeTable;
